import { supabase } from '../lib/supabase';

export interface FavoriteDrillStep {
  id: string; // favorite_drill_steps.id — stable identity for this favorite, independent of the source step
  drill_step_id: string | null; // the live drill_steps.id this was favorited from, if it still exists
  step_name: string;
  drill_id: string;
  drill_name: string;
  setup?: string;
  execution?: string;
  equipment?: string;
  video_url?: string;
  coaching_points?: string[];
}

export const FavoriteService = {
  async getFavoriteStepIds(userId: string, teamId: string): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('favorite_drill_steps')
        .select('drill_step_id')
        .eq('user_id', userId)
        .eq('team_id', teamId);

      if (error) throw error;
      return data.map(item => item.drill_step_id).filter((id): id is string => !!id);
    } catch (error) {
      console.error('Error fetching favorite step IDs:', error);
      return [];
    }
  },

  async getFavoriteDrillSteps(userId: string, teamId: string): Promise<FavoriteDrillStep[]> {
    try {
      // Favorites are self-contained snapshots (step_name/setup/execution/etc. were
      // captured when favorited), so this no longer joins to drill_steps — a favorite
      // stays fully usable even after its source step is deleted from its drill.
      const { data: favorites, error: favoritesError } = await supabase
        .from('favorite_drill_steps')
        .select('id, step_name, drill_id, drill_name, drill_step_id, setup, execution, equipment, video_url, coaching_points')
        .eq('user_id', userId)
        .eq('team_id', teamId)
        .order('created_at', { ascending: false });

      if (favoritesError) throw favoritesError;

      return favorites.map(favorite => ({
        id: favorite.id,
        drill_step_id: favorite.drill_step_id,
        step_name: favorite.step_name,
        drill_id: favorite.drill_id,
        drill_name: favorite.drill_name,
        setup: favorite.setup || '',
        execution: favorite.execution || '',
        equipment: favorite.equipment || '',
        video_url: favorite.video_url || '',
        coaching_points: favorite.coaching_points || []
      }));
    } catch (error) {
      console.error('Error fetching favorite drill steps:', error);
      return [];
    }
  },

  async toggleFavoriteStep(userId: string, teamId: string, stepId: string): Promise<boolean> {
    try {
      // Check if the step is already favorited using maybeSingle to avoid errors
      const { data: existingFavorite, error: checkError } = await supabase
        .from('favorite_drill_steps')
        .select('id')
        .eq('user_id', userId)
        .eq('team_id', teamId)
        .eq('drill_step_id', stepId)
        .maybeSingle(); // Use maybeSingle() instead of single() to avoid errors when no record found

      // If there's an error, it's a real error (maybeSingle doesn't throw PGRST116)
      if (checkError) {
        console.error('Error checking existing favorite:', checkError);
        throw checkError;
      }

      if (existingFavorite) {
        // Remove from favorites
        const { error } = await supabase
          .from('favorite_drill_steps')
          .delete()
          .eq('id', existingFavorite.id);

        if (error) {
          console.error('Error removing from favorites:', error);
          throw error;
        }

        return false; // Indicates item was removed
      } else {
        // Add to favorites - snapshot the step's full content so this favorite
        // remains usable even after the source step is later deleted from its drill.
        const { data: step, error: stepError } = await supabase
          .from('drill_steps')
          .select('id, name, drill_id, setup, execution, equipment, video_url')
          .eq('id', stepId)
          .single();

        if (stepError) {
          console.error('Error fetching step details:', stepError);
          throw stepError;
        }

        // Get drill name
        const { data: drill, error: drillError } = await supabase
          .from('drills')
          .select('name')
          .eq('id', step.drill_id)
          .single();

        if (drillError) {
          console.error('Error fetching drill details:', drillError);
          throw drillError;
        }

        // Get coaching points for this step
        const { data: coachingPoints, error: cpError } = await supabase
          .from('drill_coaching_points')
          .select('point, order_index')
          .eq('drill_step_id', stepId)
          .order('order_index');

        const points = cpError ? [] : coachingPoints.map(cp => cp.point);

        // Build the favorite record
        const favoriteRecord = {
          user_id: userId,
          team_id: teamId,
          drill_step_id: stepId,
          step_name: step.name,
          drill_id: step.drill_id,
          drill_name: drill.name,
          setup: step.setup,
          execution: step.execution,
          equipment: step.equipment,
          video_url: step.video_url,
          coaching_points: points
        };

        // Add to favorites
        const { error } = await supabase
          .from('favorite_drill_steps')
          .insert(favoriteRecord);

        if (error) {
          console.error('Error inserting favorite:', error);
          throw error;
        }

        return true; // Indicates item was added
      }
    } catch (error) {
      console.error('Error toggling favorite step:', error);
      throw error;
    }
  },

  /**
   * Remove a favorite directly by its own id. Unlike toggleFavoriteStep (which looks
   * the favorite up by its live drill_step_id), this works even for favorites whose
   * source step was since deleted from its drill — the only way those can be removed.
   */
  async removeFavorite(favoriteId: string): Promise<void> {
    const { error } = await supabase
      .from('favorite_drill_steps')
      .delete()
      .eq('id', favoriteId);

    if (error) {
      console.error('Error removing favorite:', error);
      throw error;
    }
  },

  async getCurrentUserId(): Promise<string> {
    const { data: userData } = await supabase.auth.getUser();
    return userData?.user?.id || 'anonymous';
  }
};
